// D110 (HB-A §1.4) — the landing ring. While a ball hangs in the air and belongs to nobody, both teams
// see a ring on the pitch where it will come down: real football reads flight, and without the read a
// cross is a lottery. Presentation only (§13.5) — this never enters the sim; it re-flies the SAME §5.3
// model the server integrates (shared/sim/aerial), so the ring is where the ball actually lands.
//
// Pure: takes a ViewBall and returns a point or null. The scene decides how to draw it.

import { ballLanding, strikeEnvelope, type PitchCondition, type SimConfig, type StrikeKind } from '@fafi/shared';
import type { ViewBall, ViewState } from '../game/viewState';

/** The height at which a ball counts as "in the air" and worth marking. */
export const LANDING_MIN_Z = 0.6; // above trap height (CLAIM_Z × 1.5 = 0.75) is a genuine high ball

export interface LandingRing {
  readonly x: number;
  readonly y: number;
  /** 0..1 — how close the ball is to touching down; the scene fades/tightens the ring with it. */
  readonly progress01: number;
}

/**
 * Where the ring goes this frame, or null when there is nothing to mark (ball carried, or rolling on
 * the deck). `progress01` is the ball's height relative to the flight's own apex, inverted — 0 while it
 * hangs at the top, 1 as it arrives — so the ring tightens as the ball drops onto it.
 */
export function landingRing(ball: ViewBall, cfg: SimConfig, conditions: PitchCondition, apexHint = 0): LandingRing | null {
  const point = ballLanding(ball, cfg, conditions, LANDING_MIN_Z);
  if (point === null) return null;
  const top = Math.max(apexHint, ball.pos.z, 1e-6);
  const progress01 = Math.max(0, Math.min(1, 1 - ball.pos.z / top));
  return { x: point.x, y: point.y, progress01 };
}

/**
 * D113 (HB-D §1.4) — the header-ready cue: which aerial verb YOUR player could finish with right now,
 * or null. Reads the shared `strikeEnvelope`, which is the very function the sim resolves the strike
 * from, so the cue can never promise a header the sim would refuse. Presentation only.
 */
export function aerialCue(view: ViewState, cfg: SimConfig): StrikeKind | null {
  const own = view.entities.find((e) => e.isControlled);
  if (!own) return null;
  return strikeEnvelope(view.ball, own.pos, cfg, view.conditions);
}
